import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar, ThumbsUp } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSeo, organizationSchema, breadcrumbSchema } from "../lib/useSeo";
import { ARTICLES, CATEGORIES } from "../lib/articles";
import { CTASection } from "../components/sections/CTASection";

const fmtDate = (iso, locale) =>
  new Date(iso).toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const readHelpfulCount = (slug) => {
  try {
    const raw = localStorage.getItem(`art-helpful-${slug}`);
    return raw ? JSON.parse(raw) : { up: 0, down: 0 };
  } catch {
    return { up: 0, down: 0 };
  }
};

export default function BlogPage() {
  const { locale } = useI18n();
  const isDE = locale === "de";

  useSeo({
    title: isDE
      ? "Blog — Insights zu AI, Webentwicklung, SEO & Strategie | Joshi FusionTech Media"
      : "Blog — insights on AI, web development, SEO & strategy | Joshi FusionTech Media",
    description: isDE
      ? "Tiefgehende Artikel zu KI-Automatisierung, Webentwicklung, SEO, Digital Marketing und Strategie für deutsche Unternehmen."
      : "Deep-dive articles on AI automation, web development, SEO, digital marketing and strategy for German businesses.",
    canonical: "https://www.joshifusiontech.de/blog",
    locale,
    jsonLd: [
      organizationSchema(),
      breadcrumbSchema([
        { name: isDE ? "Start" : "Home", url: "https://www.joshifusiontech.de/" },
        { name: "Blog", url: "https://www.joshifusiontech.de/blog" },
      ]),
    ],
  });

  const sorted = [...ARTICLES].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  const featured = sorted[0];
  const rest = sorted.slice(1);
  const mostHelpful = [...ARTICLES]
    .map((a) => ({ ...a, _count: readHelpfulCount(a.slug).up }))
    .sort((a, b) => b._count - a._count)
    .slice(0, 3);

  return (
    <main data-testid="blog-page" className="pt-32 pb-24">
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 mb-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-px bg-brand-orange" />
            <span className="label-mono">Blog</span>
          </div>
          <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-[1.05]">
            {isDE ? "Tiefe Einblicke. Keine Phrasen." : "Deep insights. No fluff."}
          </h1>
          <p className="mt-6 text-white/65 text-lg md:text-xl max-w-2xl leading-relaxed">
            {isDE
              ? "Praxisartikel zu KI-Automatisierung, Webentwicklung, SEO, Marketing und Strategie — geschrieben für Entscheider:innen, nicht für SEO-Bots."
              : "Practical articles on AI automation, web development, SEO, marketing and strategy — written for decision-makers, not SEO bots."}
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="label-mono mb-5">{isDE ? "Kategorien" : "Categories"}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/blog/${c.slug}`}
                data-testid={`blog-category-${c.slug}`}
                className="glass rounded-2xl px-4 py-4 hover:border-brand-orange/40 transition-all group"
              >
                <div className="text-sm text-white/90 group-hover:text-brand-orange transition-colors font-medium">
                  {c.label[locale]}
                </div>
                <div className="text-[11px] text-white/45 mt-1 leading-snug">{c.tagline[locale]}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured article */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          <Link
            to={`/blog/${featured.category}/${featured.slug}`}
            data-testid="blog-featured"
            className="grid lg:grid-cols-[1.2fr_1fr] gap-8 items-stretch glass rounded-3xl overflow-hidden hover:border-brand-orange/40 transition-all group"
          >
            <div className="relative h-72 lg:h-full min-h-[320px] overflow-hidden">
              <img
                src={`/blog/${featured.slug}.webp`}
                alt={featured.title[locale]}
                loading="eager"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute top-5 left-5 px-2.5 py-1 rounded-md bg-brand-orange/90 text-white text-[10px] font-mono uppercase tracking-[0.22em] shadow-lg">
                {CATEGORIES.find((c) => c.slug === featured.category)?.label[locale]}
              </div>
              <div className="absolute bottom-5 left-5 text-[10px] font-mono uppercase tracking-[0.22em] text-white/75">
                Featured
              </div>
            </div>
            <div className="p-7 md:p-10">
              <div className="flex items-center gap-4 text-xs text-white/45 mb-4">
                <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {featured.readMinutes} min</span>
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmtDate(featured.publishedAt, locale)}</span>
              </div>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight leading-[1.1] group-hover:text-brand-orange transition-colors">
                {featured.title[locale]}
              </h2>
              <p className="mt-4 text-white/65 leading-relaxed">{featured.excerpt[locale]}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm text-brand-orange">
                {isDE ? "Artikel lesen" : "Read article"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* All articles */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight">
              {isDE ? "Alle Artikel" : "All articles"}
            </h2>
            <span className="label-mono">{rest.length + 1} {isDE ? "Beiträge" : "posts"}</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((a) => {
              const catLabel = CATEGORIES.find((c) => c.slug === a.category)?.label[locale];
              return (
                <Link
                  key={a.slug}
                  to={`/blog/${a.category}/${a.slug}`}
                  data-testid={`blog-card-${a.slug}`}
                  className="glass rounded-2xl overflow-hidden hover:border-brand-orange/40 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(255,106,0,0.15)] transition-all duration-300 group flex flex-col"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={`/blog/${a.slug}.webp`}
                      alt={a.title[locale]}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-brand-orange/90 text-white text-[10px] font-mono uppercase tracking-[0.22em] shadow-lg">
                      {catLabel}
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-[11px] text-white/45 mb-3">
                      <span>{fmtDate(a.publishedAt, locale)}</span>
                      <span>·</span>
                      <span>{a.readMinutes} min</span>
                    </div>
                    <h3 className="font-display font-semibold text-lg text-white tracking-tight leading-[1.2] group-hover:text-brand-orange transition-colors flex-1">
                      {a.title[locale]}
                    </h3>
                    <p className="mt-3 text-sm text-white/60 line-clamp-3">{a.excerpt[locale]}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Most helpful */}
      {mostHelpful[0]?._count > 0 && (
        <section className="px-6 md:px-12 lg:px-24 mb-20">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <ThumbsUp className="w-4 h-4 text-brand-orange" />
              <span className="label-mono">{isDE ? "Am hilfreichsten" : "Most helpful"}</span>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {mostHelpful.map((a) => (
                <Link key={a.slug} to={`/blog/${a.category}/${a.slug}`} className="glass rounded-2xl p-5 hover:border-brand-orange/40 transition-all">
                  <div className="text-sm text-white/85 font-display font-semibold">{a.title[locale]}</div>
                  <div className="mt-2 text-[11px] text-white/50">{a._count} {isDE ? "Leser:innen fanden diesen Artikel hilfreich" : "readers found this helpful"}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </main>
  );
}
